import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './CalendarList.css';

export default function CalendarList() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [calendars, setCalendars] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [lastAccessed, setLastAccessed] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [iconInfoCalendarId, setIconInfoCalendarId] = useState(null);
  const fileInputRef = useRef(null);
  const pendingCalendarIdRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;

    // Listen to user's favorites and last accessed
    const userUnsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        setFavorites(userData.favoriteCalendars || []);
        setLastAccessed(userData.lastAccessed || {});
      }
    });

    // Real-time listener for calendars where user is a member
    const calendarsRef = collection(db, 'calendars');
    const q = query(
      calendarsRef,
      where('members', 'array-contains', currentUser.uid)
    );

    const calendarsUnsubscribe = onSnapshot(q, (snapshot) => {
      const calendarsList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(cal => cal.members && cal.members.includes(currentUser.uid));
      setCalendars(calendarsList);
      setLoading(false);
    });

    return () => {
      userUnsubscribe();
      calendarsUnsubscribe();
    };
  }, [currentUser]);

  async function toggleFavorite(calendarId, e) {
    e.stopPropagation(); // Prevent card click

    try {
      const userRef = doc(db, 'users', currentUser.uid);

      if (favorites.includes(calendarId)) {
        // Remove from favorites
        await updateDoc(userRef, {
          favoriteCalendars: arrayRemove(calendarId)
        });
      } else {
        // Add to favorites
        await updateDoc(userRef, {
          favoriteCalendars: arrayUnion(calendarId)
        });
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
    }
  }

  function handleCalendarClick(calendarId) {
    navigate(`/calendar/${calendarId}`);
  }

  function handleIconClick(calendarId, isAdmin, e) {
    e.stopPropagation();
    if (isAdmin && !uploadingId) {
      setIconInfoCalendarId(iconInfoCalendarId === calendarId ? null : calendarId);
    }
  }

  function handleChooseFile(calendarId, e) {
    e.stopPropagation();
    pendingCalendarIdRef.current = calendarId;
    setIconInfoCalendarId(null);
    fileInputRef.current.click();
  }

  // Compress image to base64 (fit within 128x128, maintain aspect ratio)
  function compressImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 128;
          canvas.width = maxSize;
          canvas.height = maxSize;
          const ctx = canvas.getContext('2d');

          // Fill with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, maxSize, maxSize);

          // Fit image within square, maintaining aspect ratio
          const scale = Math.min(maxSize / img.width, maxSize / img.height);
          const w = img.width * scale;
          const h = img.height * scale;
          const x = (maxSize - w) / 2;
          const y = (maxSize - h) / 2;
          ctx.drawImage(img, x, y, w, h);

          const base64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleFileChange(e) {
    const file = e.target.files[0];
    const calendarId = pendingCalendarIdRef.current;
    if (!file || !calendarId) return;

    // Reset file input so same file can be selected again
    e.target.value = '';

    // Validate file
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    try {
      setUploadingId(calendarId);

      // Compress and convert to base64
      const base64Icon = await compressImage(file);

      // Save directly to Firestore
      await updateDoc(doc(db, 'calendars', calendarId), { icon: base64Icon });
    } catch (error) {
      console.error('Error uploading icon:', error);
    } finally {
      setUploadingId(null);
      pendingCalendarIdRef.current = null;
    }
  }

  if (loading) {
    return (
      <div className="calendars-loading">
        <div className="spinner"></div>
        <p>جاري تحميل التقاويم...</p>
      </div>
    );
  }

  if (calendars.length === 0) {
    return null;
  }

  // Sort calendars: favorites first, then by last accessed (most recent first)
  const sortedCalendars = [...calendars].sort((a, b) => {
    const aIsFavorite = favorites.includes(a.id);
    const bIsFavorite = favorites.includes(b.id);

    // Favorites come first
    if (aIsFavorite && !bIsFavorite) return -1;
    if (!aIsFavorite && bIsFavorite) return 1;

    // Sort by last accessed time (most recent first)
    const aLastAccessed = lastAccessed[a.id] ? new Date(lastAccessed[a.id]).getTime() : 0;
    const bLastAccessed = lastAccessed[b.id] ? new Date(lastAccessed[b.id]).getTime() : 0;

    if (aLastAccessed === 0 && bLastAccessed === 0) {
      // If neither has been accessed, sort by creation date
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bDate - aDate;
    }

    return bLastAccessed - aLastAccessed; // Most recent first
  });

  // Show only 2 calendars by default, or all if showAll is true
  const displayedCalendars = showAll ? sortedCalendars : sortedCalendars.slice(0, 2);
  const hasMore = calendars.length > 2;

  return (
    <div className="calendars-section">
      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />

      <div className="calendars-header">
        <h2 className="calendars-title">تقاويمي</h2>
        <span className="calendars-count">{calendars.length}</span>
      </div>

      <div className="calendars-grid">
        {displayedCalendars.map(calendar => {
          const isAdmin = calendar.createdBy === currentUser.uid;
          const isFavorite = favorites.includes(calendar.id);
          const memberCount = calendar.members?.length || 0;
          const isUploading = uploadingId === calendar.id;

          return (
            <div
              key={calendar.id}
              className="calendar-card"
              onClick={() => handleCalendarClick(calendar.id)}
            >
              <div className="calendar-card-header">
                <div
                  className={`calendar-icon ${isAdmin ? 'editable' : ''} ${isUploading ? 'uploading' : ''}`}
                  onClick={(e) => handleIconClick(calendar.id, isAdmin, e)}
                  title={isAdmin ? 'تغيير الأيقونة' : ''}
                >
                  {isUploading ? (
                    <div className="icon-spinner"></div>
                  ) : calendar.icon && (calendar.icon.startsWith('http') || calendar.icon.startsWith('data:')) ? (
                    <img src={calendar.icon} alt="" className="calendar-icon-img" />
                  ) : (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  )}
                  {isAdmin && !isUploading && (
                    <div className="icon-edit-badge">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </div>
                  )}
                  {iconInfoCalendarId === calendar.id && (
                    <>
                      <div className="icon-info-overlay" onClick={(e) => { e.stopPropagation(); setIconInfoCalendarId(null); }} />
                      <div className="icon-info-popup" onClick={(e) => e.stopPropagation()}>
                        <h4>تغيير أيقونة المجموعة</h4>
                        <ul>
                          <li>الحجم النهائي: 128×128 بكسل</li>
                          <li>الصيغ المدعومة: PNG, JPG, WEBP</li>
                          <li>الحد الأقصى: 5 ميجابايت</li>
                          <li>يُفضل صورة مربعة (يتم قصها تلقائياً)</li>
                        </ul>
                        <button className="icon-info-btn" onClick={(e) => handleChooseFile(calendar.id, e)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                          اختر صورة
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="calendar-badges">
                  {isAdmin && (
                    <span className="admin-badge">مسؤول</span>
                  )}
                  <button
                    className={`favorite-btn ${isFavorite ? 'active' : ''}`}
                    onClick={(e) => toggleFavorite(calendar.id, e)}
                    title={isFavorite ? 'إزالة من المفضلة' : 'إضافة للمفضلة'}
                  >
                    {isFavorite ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <h3 className="calendar-name">{calendar.name}</h3>

              <div className="calendar-info">
                <div className="info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span>{memberCount} عضو</span>
                </div>

                <div className="info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                    <polyline points="10 17 15 12 10 7"></polyline>
                    <line x1="15" y1="12" x2="3" y2="12"></line>
                  </svg>
                  <span>{calendar.inviteCode}</span>
                </div>
              </div>

              <div className="calendar-actions">
                <button className="view-btn">
                  عرض التقويم
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Show More / Show Less Button */}
      {hasMore && (
        <button
          className="show-more-btn"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? (
            <>
              عرض أقل
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
            </>
          ) : (
            <>
              عرض المزيد ({calendars.length - 2})
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </>
          )}
        </button>
      )}
    </div>
  );
}